var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Solution_1;
import { JSON, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ChurnTemplateConstants } from './churnTemplateConstants';
import { DiversityTemplateConstants } from './diversityTemplateConstants';
import { ForecastTemplateConstants } from './forecastTemplateConstants';
import { GenderPayGapTemplateConstants } from './genderPayGapTemplateConstants';
import './params/solutionParams'; // force import for JsonSchema
import { SellerChurnTemplateConstants } from './sellerChurnTemplateConstants';
import { SellerPerformanceTemplateConstants } from './sellerPerformanceTemplateConstants';
import './solutionColumnNameMap'; // force import for JsonSchema
import { LatestSolutionVersions, SolutionType } from './solutionType';
import { BenchmarkingTemplateConstants } from './benchmarkingTemplateConstants';
const schema = {
    $id: 'urn:wisepipe-solution-domain:Solution',
    type: 'object',
    properties: {
        solutionID: { type: 'string', minLength: 1, maxLength: 200 },
        name: { type: 'string', minLength: 1, maxLength: 100 },
        type: { type: 'number', minimum: 0 },
        dataSourceID: { type: 'string', uid: 9 },
        pipeID: { type: 'string', uid: 10 },
        params: { $ref: 'urn:wisepipe-solution-domain:SolutionParams' },
        createdAt: { type: 'string', format: 'date-time' },
        version: { type: 'number', minimum: 0 },
        mapping: { $ref: 'urn:wisepipe-solution-domain:SolutionColumnNameMap' }
    },
    // Note: version not required here to support really old versions of the app that don't have version yet.
    required: ['solutionID', 'type', 'name', 'pipeID', 'dataSourceID', 'params']
};
const NameByType = {
    [SolutionType.Churn]: 'Customer Churn',
    [SolutionType.Forecast]: 'Demand Forecasting',
    [SolutionType.CrossSell]: 'Cross Selling',
    [SolutionType.Journey]: 'Customer Journey',
    [SolutionType.Attribution]: 'Marketing Attribution',
    [SolutionType.LifetimeValue]: 'Customer Lifetime Value',
    [SolutionType.Segmentation]: 'Customer Segmentation',
    [SolutionType.LeadScoring]: 'Lead Scoring',
    [SolutionType.GenderPayGap]: 'Gender Pay',
    [SolutionType.SellerChurn]: 'Seller Churn',
    [SolutionType.SellerPerformance]: 'Seller Performance',
    [SolutionType.AccrualsForecasting]: 'Accruals Forecasting',
    [SolutionType.Diversity]: 'Diversity',
    [SolutionType.Benchmarking]: 'Sales Benchmarking'
};
let Solution = Solution_1 = class Solution {
    constructor(solutionID, name, type, dataSourceID, pipeID, params, version, mapping) {
        this.solutionID = solutionID;
        this.type = type;
        this.name = name;
        this.pipeID = pipeID;
        this.dataSourceID = dataSourceID;
        this.params = params;
        this.version = version;
        this.mapping = mapping;
        JSON.validate(this, Solution_1);
    }
    static getWidgetOutputNames(type) {
        switch (type) {
            case SolutionType.Churn:
                return [ChurnTemplateConstants.RiskSummaryOutput];
            case SolutionType.Forecast:
                return [ForecastTemplateConstants.HistoricalAndForecastOutput];
            case SolutionType.GenderPayGap:
                return [GenderPayGapTemplateConstants.MeanPayOutput, GenderPayGapTemplateConstants.MeanBonusOutput];
            case SolutionType.SellerChurn:
                return [SellerChurnTemplateConstants.SegmentationSummaryOutput];
            case SolutionType.SellerPerformance:
                return [SellerPerformanceTemplateConstants.AttainmentHistogramOutput];
            case SolutionType.Diversity:
                return [DiversityTemplateConstants.EthnicityOutput];
            case SolutionType.Benchmarking:
                return [BenchmarkingTemplateConstants.GrowthEfficiencyOutput];
            default:
                // This applies to any new apps created.
                throw new Error('Need at least 1 output for widget data');
        }
    }
    static getCacheOutputNames(type) {
        switch (type) {
            case SolutionType.Churn:
                return [ChurnTemplateConstants.HistoricalSummaryOutput, ChurnTemplateConstants.PredictionSummaryOutput];
            case SolutionType.Forecast:
                return [ForecastTemplateConstants.HistoricalAndForecastOutput];
            case SolutionType.GenderPayGap:
                return [
                    GenderPayGapTemplateConstants.MeanPayOutput,
                    GenderPayGapTemplateConstants.MedianPayOutput,
                    GenderPayGapTemplateConstants.MeanBonusOutput,
                    GenderPayGapTemplateConstants.MedianBonusOutput
                ];
            case SolutionType.SellerChurn:
                return [
                    SellerChurnTemplateConstants.HistoricalSummaryOutput,
                    SellerChurnTemplateConstants.PredictedChurnSummaryOutput
                ];
            case SolutionType.SellerPerformance:
                return [SellerPerformanceTemplateConstants.AttainmentHistogramOutput];
            case SolutionType.Diversity:
                return [DiversityTemplateConstants.EthnicityOutput];
            case SolutionType.Benchmarking:
                return [BenchmarkingTemplateConstants.GrowthEfficiencyOutput];
            default:
                // No outputs for cache data is fine.
                return [];
        }
    }
    static getTypeName(type) {
        return NameByType[type] ?? '';
    }
    static getLatestVersion(type) {
        return LatestSolutionVersions[type];
    }
};
Solution = Solution_1 = __decorate([
    JsonSchema(schema)
], Solution);
export { Solution };
//# sourceMappingURL=solution.js.map